import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import {
  DEFAULT_PAGE_SIZE,
  mapApiMetadataToTableColumnDefinition,
  urlEndPoint,
} from "helpers/GeneralHelper";
import { toast } from "react-toastify";

import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
  applyAsyncFilterOptionToFormState,
  applyClientAsyncFilterOptionToFormState,
  buildAsyncFilterInitialOption,
  buildClientAsyncFilterInitialOption,
  getAsyncFilterLabelFieldKey,
  getClientAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";

const RECRUITER_ASYNC_FILTER_FIELD = "recruiterId";
import { getCallAuditMetadata, submitCallAuditFilterData } from "model/CallAuditModel";
import CreateAuditModal from "pages/AllCommonModal/CreateAuditModal/create-audit";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const defaultTableColumns = [
  { Header: "Recruiter", accessor: "recruiterDisplayName" },
  { Header: "Candidate", accessor: "candidateDisplayName" },
  { Header: "Phone", accessor: "candidatePhone", type: "phone" },
  { Header: "Client", accessor: "clientDisplayName" },
  { Header: "Total Score", accessor: "totalScore" },
  { Header: "Audit Date From", accessor: "auditDateFrom", type: "date" },
  { Header: "Audit Date To", accessor: "auditDateTo", type: "date" },
  { Header: "Published", accessor: "isPublished", type: "boolean" },
  { Header: "Acknowledged", accessor: "isAcknowledged", type: "boolean" },
  {
    Header: "Zero Tolerance",
    accessor: "hasZeroToleranceViolation",
    type: "boolean",
  },
];

const mapCallAuditRecordsToRows = (list = []) =>
  (Array.isArray(list) ? list : []).map((item = {}) => ({
    ...item,
    _originalCallAudit: { ...item },
    id: item?.id ?? item?._id ?? "",
    recruiterDisplayName: item?.recruiterDisplayName ?? item?.recruiter ?? "",
    candidateDisplayName:
      item?.candidateDisplayName ?? item?.applicant ?? item?.candidateName ?? "",
    candidatePhone: item?.candidatePhone ?? item?.phone ?? item?.phoneNumber ?? "",
    clientDisplayName: item?.clientDisplayName ?? item?.client ?? "",
    totalScore: item?.totalScore ?? 0,
    auditDateFrom: item?.auditDateFrom ?? "",
    auditDateTo: item?.auditDateTo ?? "",
    isPublished: item?.isPublished ?? item?.published ?? false,
    isAcknowledged: item?.isAcknowledged ?? item?.acknowledged ?? false,
    hasZeroToleranceViolation: item?.hasZeroToleranceViolation ?? false,
    canEdit: Boolean(item?.canEdit),
  }));

const resolveCallAuditListFromResponse = (response = {}) => {
  const envelope =
    response?.data != null && typeof response.data === "object" ? response : response ?? {};
  const inner = envelope?.data;

  if (Array.isArray(inner)) {
    return {
      records: inner,
      pageNumber: envelope?.pageNumber,
      pageSize: envelope?.pageSize,
      totalPages: envelope?.totalPages,
      totalRecords: envelope?.totalRecords,
    };
  }

  const nested = inner && typeof inner === "object" ? inner : envelope;
  const records =
    nested?.records ??
    (Array.isArray(nested?.data) ? nested.data : Array.isArray(nested) ? nested : []);

  return {
    records: Array.isArray(records) ? records : [],
    pageNumber: nested?.pageNumber ?? envelope?.pageNumber,
    pageSize: nested?.pageSize ?? envelope?.pageSize,
    totalPages: nested?.totalPages ?? envelope?.totalPages,
    totalRecords: nested?.totalRecords ?? envelope?.totalRecords,
  };
};

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeCallAuditFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "emailContains", "clientId", "recruiterId", "fromDate", "toDate"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete out[key];
  });
  return out;
};

const noop = () => {};

const CallAudit = () => {
  const navigate = useNavigate();
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("CallAudit");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    recruiterId: "",
    [getAsyncFilterLabelFieldKey(RECRUITER_ASYNC_FILTER_FIELD)]: "",
    fromDate: "",
    toDate: "",
  });

  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    emailContains: null,
    clientId: null,
    recruiterId: null,
    fromDate: null,
    toDate: null,
  });

  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [createAuditModalOpen, setCreateAuditModalOpen] = useState(false);

  const getCallAuditList = useCallback(
    async (payloadOverrides = {}, options = {}) => {
      const { onlyPagination = false } = options;
      setLoading(true);
      try {
        const payload = onlyPagination
          ? {
              pageNumber: Number(payloadOverrides.pageNumber) || 1,
              pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
            }
          : normalizeCallAuditFilterPayload({
              pageNumber: requestPayload.pageNumber,
              pageSize: requestPayload.pageSize,
              nameContains: requestPayload.nameContains,
              emailContains: requestPayload.emailContains,
              clientId: requestPayload.clientId,
              fromDate: requestPayload.fromDate,
              toDate: requestPayload.toDate,
              ...payloadOverrides,
            });

        const response = await submitCallAuditFilterData(payload);
        const { records, pageNumber, pageSize, totalPages, totalRecords } =
          resolveCallAuditListFromResponse(response);

        setRows(mapCallAuditRecordsToRows(records));

        if (response == null || response?.success !== false) {
          setPaginationMeta({
            pageNumber: Number(pageNumber) || Number(payload?.pageNumber) || 1,
            pageSize: Number(pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
            totalPages: Number(totalPages) || 0,
            totalRecords: Number(totalRecords) || records.length,
          });
          if (onlyPagination) {
            setRequestPayload({
              pageNumber: payload.pageNumber,
              pageSize: payload.pageSize,
              nameContains: null,
              emailContains: null,
              clientId: null,
              recruiterId: null,
              fromDate: null,
              toDate: null,
            });
            setFilterForm({
              search: "",
              clientId: "",
              [getClientAsyncFilterLabelFieldKey()]: "",
              recruiterId: "",
              [getAsyncFilterLabelFieldKey(RECRUITER_ASYNC_FILTER_FIELD)]: "",
              fromDate: "",
              toDate: "",
            });
          } else {
            setRequestPayload(normalizeCallAuditFilterPayload(payload));
          }
        } else {
          setRows([]);
          if (response?.message) {
            toast.error(response.message);
          }
        }
      } catch (err) {
        toast.error(err?.message || "Unable to load call audit list");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [requestPayload]
  );

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:callAudit", () => getCallAuditMetadata());
        const meta = metaRes?.data ?? metaRes ?? {};

        const cols = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => mapApiMetadataToTableColumnDefinition(col))
          : defaultTableColumns;
        setTableMetaData(cols);
      } catch (e) {
        toast.error(e?.message || "Unable to load call audit metadata");
        setTableMetaData(defaultTableColumns);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getCallAuditList({ pageNumber: 1, pageSize }, { onlyPagination: true });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone, getCallAuditList]);

  const buildPayloadFromUpperForm = (form = {}) => {
    const search = String(form?.search || "").trim();
    return normalizeCallAuditFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(search),
      emailContains: strOrNull(search),
      clientId: strOrNull(form?.clientId),
      recruiterId: strOrNull(form?.recruiterId),
      fromDate: strOrNull(form?.fromDate),
      toDate: strOrNull(form?.toDate),
    });
  };

  const handleUpperSearch = () => {
    getCallAuditList(buildPayloadFromUpperForm(filterForm));
  };

  const handlePageChange = async (page) => {
    await getCallAuditList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getCallAuditList({ pageNumber: 1, pageSize: rows });
  };

  const getAuditRowId = (row = {}) => String(row?.id ?? row?._id ?? "").trim();

  const handleEdit = (item) => {
    const id = getAuditRowId(item);
    if (!id) {
      toast.error("Record ID not found");
      return;
    }
    navigate(`/${urlEndPoint.editcallAudit}/${id}`);
  };

  const handleView = (item) => {
    const id = getAuditRowId(item);
    if (!id) {
      toast.error("Record ID not found");
      return;
    }
    navigate(`/${urlEndPoint.viewcallAudit}/${id}`);
  };

  const callAuditActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.CallAuditCreateAccess),
      canEdit: Boolean(
        permissionData?.CallAuditEditAccess || permissionData?.CallAuditEditAccessForAllClient
      ),
      canView: Boolean(
        permissionData?.CallAuditAccess || permissionData?.AuditMenuAccess
      ),
    }),
    [permissionData]
  );

  const hasAnyCallAuditRowAction = useMemo(
    () => Object.values(callAuditActionPermissions).some(Boolean),
    [callAuditActionPermissions]
  );

  const tableRows = useMemo(() => rows, [rows]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">Call Audit</h5>
              {callAuditActionPermissions.canCreate ? (
                <Button
                  color="primary"
                  type="button"
                  onClick={() => setCreateAuditModalOpen(true)}
                >
                  + Create
                </Button>
              ) : null}
            </div>
            <hr />

            <div className="upper-filter-bar manage-user-filter-strip">
            <Row className="g-2 g-md-3 align-items-end">
              <Col xs="12" md="6" lg={3}>
                <span className="manage-user-filter-label">Search</span>
                <Input
                  type="text"
                  name="search"
                  placeholder="Search by Email/Company"
                  className="border rounded w-100 category-search-input"
                  value={filterForm.search}
                  onChange={(e) => {
                    const nextValue = e?.target?.value || "";
                    setFilterForm((prev) => ({ ...prev, search: nextValue }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUpperSearch();
                    }
                  }}
                />
              </Col>
              <Col xs="12" md="6" lg={2}>
                <span className="manage-user-filter-label">Client</span>
                <AsyncClientSelect
                  key={`call-audit-client-${filterForm.clientId}`}
                  pageName="CallAudit"
                  value={filterForm.clientId}
                  initialOption={buildClientAsyncFilterInitialOption(
                    filterForm.clientId,
                    filterForm[getClientAsyncFilterLabelFieldKey()]
                  )}
                  onChange={(option) =>
                    setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                  }
                  placeholder="Select Client"
                />
              </Col>
              <Col xs="12" md="6" lg={2}>
                <span className="manage-user-filter-label">Recruiter</span>
                <AsyncUserSelect
                  key={`call-audit-recruiter-${filterForm.recruiterId}`}
                  pageName="CallAudit"
                  value={filterForm.recruiterId}
                  initialOption={buildAsyncFilterInitialOption(
                    RECRUITER_ASYNC_FILTER_FIELD,
                    filterForm.recruiterId,
                    filterForm
                  )}
                  onChange={(option) =>
                    setFilterForm((prev) =>
                      applyAsyncFilterOptionToFormState(
                        prev,
                        RECRUITER_ASYNC_FILTER_FIELD,
                        option
                      )
                    )
                  }
                  placeholder="Select Recruiter"
                />
              </Col>
              <Col xs="12" md="6" lg={2}>
                <span className="manage-user-filter-label">From date</span>
                <Input
                  type="date"
                  name="fromDate"
                  className="border rounded w-100 category-search-input"
                  value={filterForm.fromDate}
                  onChange={(e) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      fromDate: e?.target?.value || "",
                    }))
                  }
                />
              </Col>
              <Col xs="12" md="6" lg={2}>
                <span className="manage-user-filter-label">To date</span>
                <Input
                  type="date"
                  name="toDate"
                  className="border rounded w-100 category-search-input"
                  value={filterForm.toDate}
                  onChange={(e) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      toDate: e?.target?.value || "",
                    }))
                  }
                />
              </Col>
              <Col xs="12" md="6" lg={2}>
                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                <Button color="primary" className="w-100 manage-user-search-btn" type="button" onClick={handleUpperSearch}>
                  <i className="fa fa-search me-1" />
                  Search
                </Button>
              </Col>
            </Row>
            </div>

            {loading ? (
              <Loaders />
            ) : tableMetaData?.length ? (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={tableRows}
                isGlobalFilter={false}
                isSort={true}
                isActionRequired={hasAnyCallAuditRowAction}
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                pageName="CallAudit"
                isCheckboxLeftSide={false}
                enableBulkDeactivate={false}
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={noop}
                setCommonResetFilter={noop}
                allCommonFilter={false}
                onEdit={
                  callAuditActionPermissions.canEdit ? handleEdit : undefined
                }
                onView={
                  callAuditActionPermissions.canView ? handleView : undefined
                }
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>

        <CreateAuditModal
          createAuditModalOpen={createAuditModalOpen}
          setCreateAuditModalOpen={setCreateAuditModalOpen}
          onSuccess={() =>
            getCallAuditList({
              pageNumber: paginationMeta.pageNumber || 1,
              pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
            })
          }
        />
      </Container>
    </div>
  );
};

CallAudit.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(CallAudit);
